<template>
  <div class="space-y-2">
    <label
      v-if="label"
      class="block text-sm font-medium text-gray-700 dark:text-gray-300"
    >
      {{ label }}
    </label>

    <div class="flex items-center space-x-4">
      <div
        class="overflow-hidden relative w-32 h-32 bg-gray-100 rounded-lg dark:bg-gray-800"
      >
        <img
          v-if="previewUrl"
          :src="previewUrl"
          class="object-cover w-full h-full"
          :alt="label || 'Image preview'"
        />
        <div
          v-else
          class="flex justify-center items-center w-full h-full text-gray-400 dark:text-gray-600"
        >
          <svg
            class="w-12 h-12"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
        </div>
      </div>

      <div class="flex-1">
        <input
          type="file"
          :accept="accept"
          class="hidden"
          ref="fileInput"
          @change="handleFileChange"
        />
        <button
          type="button"
          class="px-4 py-2 mb-2 text-sm font-medium text-white bg-green-600 rounded-md sm:mb-0 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 dark:bg-green-500 dark:hover:bg-green-600"
          @click="$refs.fileInput.click()"
          :disabled="isCompressing"
        >
          <span v-if="isCompressing" class="flex items-center">
            <svg class="mr-2 w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Compressing...
          </span>
          <span v-else>
            {{ previewUrl ? 'Change Image' : 'Upload Image' }}
          </span>
        </button>
        <button
          v-if="previewUrl"
          type="button"
          class="px-4 py-2 ml-2 text-sm font-medium text-gray-700 bg-white rounded-md border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700"
          @click="removeImage"
          :disabled="isCompressing"
        >
          Remove
        </button>
      </div>
    </div>

    <!-- File size info -->
    <div v-if="fileInfo" class="text-sm text-gray-600 dark:text-gray-400">
      <p>Original: {{ fileInfo.originalSize }}</p>
      <p v-if="fileInfo.compressedSize && fileInfo.compressedSize !== fileInfo.originalSize">
        Compressed: {{ fileInfo.compressedSize }}
        <span class="text-green-600">({{ fileInfo.compressionRatio }}% smaller)</span>
      </p>
    </div>

    <p
      v-if="error"
      class="mt-1 text-sm text-red-600 dark:text-red-400"
    >
      {{ error }}
    </p>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue';
import { compressImage, formatFileSize } from '@/utils/imageCompression';

const props = defineProps({
  modelValue: {
    type: [File, String, null],
    default: null,
  },
  label: {
    type: String,
    default: '',
  },
  accept: {
    type: String,
    default: 'image/*',
  },
  error: {
    type: String,
    default: '',
  },
  // Compression options
  compressionOptions: {
    type: Object,
    default: () => ({
      maxWidth: 1200,
      maxHeight: 1200,
      quality: 0.8,
      maxSizeMB: 1
    })
  },
  enableCompression: {
    type: Boolean,
    default: true
  }
});

const emit = defineEmits(['update:modelValue', 'error']);

const fileInput = ref(null);
const previewUrl = ref('');
const isCompressing = ref(false);
const fileInfo = ref(null);

const handleFileChange = async (event) => {
  const file = event.target.files[0];
  if (!file) return;

  // Check file size (5MB limit)
  if (file.size > 5 * 1024 * 1024) {
    emit('update:modelValue', null);
    previewUrl.value = '';
    emit('error', 'Image size exceeds the 5MB limit');
    return;
  }

  // Check file type
  const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
  if (!validTypes.includes(file.type)) {
    emit('update:modelValue', null);
    previewUrl.value = '';
    emit('error', 'Invalid file type. Please upload a JPG, PNG, GIF, or WEBP image');
    return;
  }

  try {
    isCompressing.value = true;
    emit('error', ''); // Clear any previous errors

    let finalFile = file;
    const originalSize = formatFileSize(file.size);

    // Compress image if enabled
    if (props.enableCompression) {
      try {
        finalFile = await compressImage(file, props.compressionOptions);
        const compressedSize = formatFileSize(finalFile.size);
        const compressionRatio = Math.round(((file.size - finalFile.size) / file.size) * 100);

        fileInfo.value = {
          originalSize,
          compressedSize,
          compressionRatio
        };
      } catch (compressionError) {
        console.warn('Image compression failed, using original file:', compressionError);
        finalFile = file;
        fileInfo.value = {
          originalSize,
          compressedSize: originalSize,
          compressionRatio: 0
        };
      }
    } else {
      fileInfo.value = {
        originalSize,
        compressedSize: originalSize,
        compressionRatio: 0
      };
    }

    emit('update:modelValue', finalFile);
    previewUrl.value = URL.createObjectURL(finalFile);

  } catch (error) {
    console.error('Error processing image:', error);
    emit('error', 'Failed to process image. Please try again.');
    emit('update:modelValue', null);
    previewUrl.value = '';
  } finally {
    isCompressing.value = false;
  }
};

const removeImage = () => {
  emit('update:modelValue', null);
  previewUrl.value = '';
  fileInfo.value = null;
  if (fileInput.value) {
    fileInput.value.value = '';
  }
};

watch(
  () => props.modelValue,
  (newValue) => {
    if (newValue instanceof File) {
      previewUrl.value = URL.createObjectURL(newValue);
      fileInfo.value = {
        originalSize: formatFileSize(newValue.size),
        compressedSize: formatFileSize(newValue.size),
        compressionRatio: 0
      };
    } else if (typeof newValue === 'string') {
      previewUrl.value = newValue;
      fileInfo.value = null;
    } else {
      previewUrl.value = '';
      fileInfo.value = null;
    }
  },
  { immediate: true }
);
</script>
